/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { EstimateShipping as EstimateShippingComponent } from '@/cart/components';
import { useEstimatedShipping } from '@/cart/hooks';
import { setEstimatedShippingLocation } from '@/cart/lib/persisted-data';
import { state } from '@/cart/lib/state';
import { Button, Input, Picker, Price } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Container } from '@adobe-commerce/elsie/lib';
import { events } from '@adobe-commerce/event-bus';
import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from 'preact/compat';

export interface EstimateShippingProps extends HTMLAttributes<HTMLDivElement> {
  showDefaultEstimatedShippingCost: boolean;
}

export const EstimateShipping: Container<EstimateShippingProps> = ({
  showDefaultEstimatedShippingCost,
}) => {
  const [isVirtual, setIsVirtual] = useState(false);

  const {
    loading,
    countries,
    regions,
    selectedCountry,
    estimatedDestinationText,
    estimatedShippingPrice,
    handleCountrySelected,
    handleEstimateShipping,
    regionsLoading,
    selectedRegion,
    selectedZip,
    shippingEstimated,
    resetValues,
  } = useEstimatedShipping();
  const handleEstimations = useCallback(
    (formValues: any) => {
      handleEstimateShipping(formValues).then(() => {
        setEstimatedShippingLocation(formValues);
      });
    },
    [handleEstimateShipping]
  );
  useEffect(() => {
    const cartEvent = events.on(
      'cart/data',
      (payload) => {
        setIsVirtual(payload?.isVirtual || false);

        const shippingAddress = payload?.addresses?.shipping?.[0];

        if (showDefaultEstimatedShippingCost && !shippingAddress) {
          handleEstimateShipping({
            shippingCountry: state.config?.defaultCountry ?? '',
          });
        }
        if (!shippingAddress) return;
        const { countryCode, regionCode, zipCode } = shippingAddress;

        handleEstimations({
          shippingCountry: countryCode,
          shippingState: regionCode,
          shippingZip: zipCode,
        });
      },
      { eager: true }
    );

    return () => {
      cartEvent?.off();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const updatedEvent = events.on('cart/updated', () => {
      if (!shippingEstimated) return;
      handleEstimateShipping({
        shippingCountry: selectedCountry,
        shippingState: selectedRegion,
        shippingZip: selectedZip,
      });
    });

    return () => {
      updatedEvent?.off();
    };
  }, [
    shippingEstimated,
    selectedCountry,
    selectedRegion,
    selectedZip,
    handleEstimateShipping,
  ]);

  useEffect(() => {
    const resetEvent = events.on('cart/reset', () => {
      resetValues();
      setEstimatedShippingLocation(null);
    });

    return () => {
      resetEvent?.off();
    };
  }, [resetValues]);

  useEffect(() => {
    const mergeEvent = events.on('cart/merged', () => {
      if (!shippingEstimated) return;

      handleEstimations({
        shippingCountry: selectedCountry,
        shippingState: selectedRegion,
        shippingZip: selectedZip,
      });
    });

    return () => {
      mergeEvent?.off();
    };
  }, [
    shippingEstimated,
    selectedCountry,
    selectedRegion,
    selectedZip,
    handleEstimations,
  ]);

  const dictionary = useText({
    applyButton: 'Cart.PriceSummary.estimatedShippingForm.apply.label',
    applyButtonAriaLabel:
      'Cart.PriceSummary.estimatedShippingForm.apply.ariaLabel',
    countryField: 'Cart.PriceSummary.estimatedShippingForm.country.placeholder',
    freeShipping: 'Cart.PriceSummary.freeShipping',
    stateField: 'Cart.PriceSummary.estimatedShippingForm.state.placeholder',
    taxToBeDetermined: 'Cart.PriceSummary.taxToBeDetermined',
    zipField: 'Cart.PriceSummary.estimatedShippingForm.zip.placeholder',
  });

  if (isVirtual) return null;

  const cartTaxesConfig = state.config?.shoppingCartDisplaySetting;
  return (
    <EstimateShippingComponent
      loading={loading}
      taxIncluded={cartTaxesConfig?.shipping === 'INCLUDING_TAX'}
      taxExcluded={cartTaxesConfig?.shipping === 'INCLUDING_EXCLUDING_TAX'}
      price={
        estimatedShippingPrice?.amount == 0 ? (
          <span data-testId="free-shipping">{dictionary.freeShipping}</span>
        ) : cartTaxesConfig?.shipping === 'INCLUDING_TAX' &&
          estimatedShippingPrice ? (
          <Price
            data-testid="shipping"
            {...estimatedShippingPrice.priceIncludingtax}
          />
        ) : estimatedShippingPrice ? (
          <Price {...estimatedShippingPrice} />
        ) : (
          <span>{dictionary.taxToBeDetermined}</span>
        )
      }
      estimated={true}
      priceExcludingTax={
        estimatedShippingPrice?.priceExcludingtax ? (
          <Price
            data-testid="shipping-excluding-tax"
            {...estimatedShippingPrice.priceExcludingtax}
          />
        ) : (
          <span>{dictionary.taxToBeDetermined}</span>
        )
      }
      countryField={
        <Picker
          autoComplete="country-name"
          name="shippingCountry"
          placeholder={dictionary.countryField}
          value={selectedCountry}
          variant="primary"
          options={countries}
          handleSelect={handleCountrySelected}
          data-testid="estimate-shipping-country-selector"
        />
      }
      stateField={
        regions.length > 0 ? (
          <Picker
            autoComplete="address-level1"
            name="shippingState"
            placeholder={dictionary.stateField}
            variant="primary"
            options={regions}
            value={selectedRegion}
            data-testid="estimate-shipping-state-selector"
            disabled={regionsLoading}
          />
        ) : (
          <Input
            aria-label={dictionary.stateField}
            autoComplete="address-level1"
            name="shippingState"
            placeholder={dictionary.stateField}
            variant="primary"
            value={selectedRegion}
            disabled={regionsLoading}
            data-testid="estimate-shipping-state-input"
            maxLength={50}
          />
        )
      }
      zipField={
        <Input
          aria-label={dictionary.zipField}
          autoComplete="postal-code"
          name="shippingZip"
          placeholder={dictionary.zipField}
          variant="primary"
          data-testid="estimate-shipping-zip-input"
          value={selectedZip}
          maxLength={12}
        />
      }
      estimateButton={
        <Button
          variant="secondary"
          data-testid="estimate-shipping-apply-button"
          aria-label={dictionary.applyButtonAriaLabel}
        >
          {dictionary.applyButton}
        </Button>
      }
      destinationText={estimatedDestinationText || dictionary.taxToBeDetermined}
      onEstimate={handleEstimations}
    />
  );
};
